import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

const JOB_TYPES = ["Full-time", "Part-time", "Temp", "Temp to Perm", "Contract"];
const emptyJob = { title: "", location: "", job_type: "Full-time", pay_rate: "", tags: "", description: "", active: true };

export const JobsManager = ({ onChanged }) => {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyJob);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/jobs").then((res) => setJobs(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyJob); setOpen(true); };
  const openEdit = (job) => {
    setEditing(job);
    setForm({ ...job, tags: (job.tags || []).join(", ") });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.location) { toast.error("Title and location are required."); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    delete payload.id; delete payload.created_at;
    try {
      if (editing) await api.put(`/admin/jobs/${editing.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(editing ? "Job updated" : "Job added");
      setOpen(false);
      load(); onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleActive = async (job) => {
    const payload = { ...job, active: !job.active };
    delete payload.id; delete payload.created_at;
    try {
      await api.put(`/admin/jobs/${job.id}`, payload);
      load(); onChanged?.();
    } catch (err) { toast.error("Failed to update job"); }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"?`)) return;
    try {
      await api.delete(`/admin/jobs/${job.id}`);
      toast.success("Job deleted");
      load(); onChanged?.();
    } catch (err) { toast.error("Failed to delete job"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="jobs-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-semibold text-slate-900">Job listings</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-job-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus size={16} className="mr-1.5" /> Add Job
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit job" : "Add a new job"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Job title *</Label>
                <Input data-testid="job-form-title" value={form.title} onChange={set("title")} placeholder="HGV Class 1 Driver" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input data-testid="job-form-location" value={form.location} onChange={set("location")} placeholder="Bristol" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.job_type} onValueChange={(v) => setForm({ ...form, job_type: v })}>
                    <SelectTrigger data-testid="job-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Pay rate</Label>
                <Input data-testid="job-form-pay" value={form.pay_rate} onChange={set("pay_rate")} placeholder="£15 - £18 / hour" />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input data-testid="job-form-tags" value={form.tags} onChange={set("tags")} placeholder="Immediate Start, Weekly Pay" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea data-testid="job-form-description" value={form.description} onChange={set("description")} rows={3} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="job-form-active" />
                <Label>Visible on website</Label>
              </div>
              <Button onClick={save} disabled={saving} data-testid="job-form-save" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? "Saving..." : editing ? "Save changes" : "Add job"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {jobs.length === 0 && <p className="text-slate-500 text-sm" data-testid="jobs-manager-empty">No jobs yet. Add your first job.</p>}
        {jobs.map((job, i) => (
          <div key={job.id} data-testid={`admin-job-row-${i}`}
            className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-medium text-slate-900">{job.title}</h3>
                {!job.active && <Badge variant="secondary" className="text-slate-500">Hidden</Badge>}
                {(job.tags || []).map((t) => <Badge key={t} className="bg-cyan-50 text-cyan-700 hover:bg-cyan-50 border border-cyan-200">{t}</Badge>)}
              </div>
              <p className="text-sm text-slate-500 mt-1">{job.location} · {job.job_type}{job.pay_rate ? ` · ${job.pay_rate}` : ""}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <Switch checked={job.active} onCheckedChange={() => toggleActive(job)} data-testid={`admin-job-toggle-${i}`} />
                <span className="text-xs text-slate-500">Live</span>
              </div>
              <Button variant="outline" size="icon" onClick={() => openEdit(job)} data-testid={`admin-job-edit-${i}`} aria-label="Edit job">
                <Pencil size={15} />
              </Button>
              <Button variant="outline" size="icon" onClick={() => remove(job)} data-testid={`admin-job-delete-${i}`}
                aria-label="Delete job" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 size={15} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
